'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';


interface Testimonial {
  id: number;
  name: string;
  role: string;
  stage: string;
  image: string;
  alt: string;
  quote: string;
  highlight: string;
}

interface ClientTestimonialsProps {
  isHydrated: boolean;
}

export default function ClientTestimonials({ isHydrated }: ClientTestimonialsProps) {
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials: Testimonial[] = [
  {
    id: 1,
    name: 'Jana Nováková',
    role: 'Marketing manažerka',
    stage: 'Po první schůzce',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fb6cf439-1763299224286.png",
    alt: 'Professional woman with brown hair in white blouse smiling at camera in modern office',
    quote: 'Konečně jsem pochopila, kam moje peníze vlastně jdou. První schůzka mi otevřela oči a ukázala mi, že finanční plánování není tak složité, jak jsem si myslela.',
    highlight: 'Jasnost a pochopení'
  },
  {
    id: 2,
    name: 'Petr Svoboda',
    role: 'IT specialista',
    stage: 'Po 3 měsících spolupráce',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17d49a222-1763294222812.png",
    alt: 'Young professional man with short dark hair in navy blue shirt smiling confidently',
    quote: 'Oceňuji hlavně trpělivost a ochotu vysvětlit vše srozumitelně. Můj finanční plán se už třikrát upravoval podle změn v mém životě a vždy to bylo bez problémů.',
    highlight: 'Flexibilita a podpora'
  },
  {
    id: 3,
    name: 'Markéta Dvořáková',
    role: 'Podnikatelka',
    stage: 'Dlouhodobá spolupráce (1 rok)',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13c1c0105-1763301747237.png",
    alt: 'Confident businesswoman with blonde hair in gray blazer in professional setting',
    quote: 'Za rok spolupráce jsem si vybudovala pevné finanční základy a konečně mám přehled. Pravidelné revize mi pomáhají držet krok se změnami a optimalizovat strategii.',
    highlight: 'Dlouhodobý růst'
  },
  {
    id: 4,
    name: 'Tomáš Černý',
    role: 'Architekt',
    stage: 'Po online diagnóze',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1b664f8f8-1763292639902.png",
    alt: 'Professional man with beard and glasses in casual business attire',
    quote: 'Online diagnóza byla skvělý první krok. Pomohla mi připravit se na schůzku a uvědomit si, co vlastně od finančního plánování očekávám.',
    highlight: 'Dobrá příprava'
  }];


  const handlePrevious = () => {
    if (!isHydrated) return;
    setCurrentIndex((prev) => prev === 0 ? testimonials.length - 1 : prev - 1);
  };

  const handleNext = () => {
    if (!isHydrated) return;
    setCurrentIndex((prev) => prev === testimonials.length - 1 ? 0 : prev + 1);
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <div className="max-w-5xl mx-auto">
      <h2 className="text-2xl lg:text-4xl font-bold text-primary mb-4 text-center font-headline">
        Zkušenosti našich klientů
      </h2>
      <p className="text-lg text-text-secondary text-center mb-12">
        Reálné příběhy z různých fází spolupráce
      </p>

      <div className="relative bg-card rounded-xl shadow-elevated p-8 lg:p-12">
        {/* Stage Badge */}
        <div className="absolute top-4 right-4 px-4 py-2 bg-accent/10 text-accent text-sm font-semibold rounded-full">
          {currentTestimonial.stage}
        </div>

        <div className="flex flex-col lg:flex-row items-center lg:items-start space-y-6 lg:space-y-0 lg:space-x-8">
          {/* Image */}
          









          {/* Content */}
          <div className="flex-1 text-center lg:text-left">
            <Icon name="ChatBubbleLeftIcon" size={32} className="text-secondary/20 mb-4 mx-auto lg:mx-0" />
            <blockquote className="text-lg lg:text-xl text-text-primary mb-6 italic">
              "{currentTestimonial.quote}"
            </blockquote>
            <div className="mb-4">
              <p className="text-lg font-semibold text-primary">{currentTestimonial.name}</p>
              <p className="text-sm text-text-secondary">{currentTestimonial.role}</p>
            </div>
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-success/10 text-success rounded-full text-sm font-medium">
              <Icon name="SparklesIcon" size={16} className="text-current" />
              <span>{currentTestimonial.highlight}</span>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-center space-x-4 mt-8">
          <button
            onClick={handlePrevious}
            className="w-10 h-10 rounded-full bg-surface hover:bg-primary/10 flex items-center justify-center transition-smooth"
            aria-label="Previous testimonial">

            <Icon name="ChevronLeftIcon" size={20} className="text-primary" />
          </button>

          {/* Dots */}
          <div className="flex space-x-2">
            {testimonials.map((_, index) =>
            <button
              key={index}
              onClick={() => isHydrated && setCurrentIndex(index)}
              className={`w-2 h-2 rounded-full transition-smooth ${
              isHydrated && index === currentIndex ? 'bg-primary w-8' : 'bg-border'}`
              }
              aria-label={`Go to testimonial ${index + 1}`} />

            )}
          </div>

          <button
            onClick={handleNext}
            className="w-10 h-10 rounded-full bg-surface hover:bg-primary/10 flex items-center justify-center transition-smooth"
            aria-label="Next testimonial">

            <Icon name="ChevronRightIcon" size={20} className="text-primary" />
          </button>
        </div>
      </div>
    </div>);

}